import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { Field, Input, Toggle } from 'ui';
import { forceEbaySync, postSyncStatus, unlinkEbayItem } from '~/api/ebay';
import LoadingCapableButton from '~/components/Form/LoadingCapableButton';
import ListFlowSection from '~/components/ListFlow/ListFlowSection';
import { useListFlowItem } from '~/hooks/listFlow/useListFlowItem';
import useHasFeature from '~/hooks/useHasFeature';
import { changedEbayId, getListFlowEbayId } from '~/store/listFlow/slices/info';
import {
  getListFlowFieldErrorMap,
  getListFlowItemId,
} from '~/store/listFlow/ui';
import { useAppDispatch, useAppSelector } from '~/store/store';

interface Props {}

const ListFlowEbaySection: React.FC<Props> = () => {
  const canUseEbaySync = useHasFeature('ebay_integration');
  const dispatch = useAppDispatch();
  const id = useAppSelector(getListFlowItemId);
  const ebayId = useAppSelector(getListFlowEbayId);
  const { data: item } = useListFlowItem();
  const errorMap = useAppSelector(getListFlowFieldErrorMap);
  const queryClient = useQueryClient();

  const mutationOptions = {
    onSuccess: async () => {
      await queryClient.invalidateQueries(['items', item?.id]);
    },
  };

  const { isLoading: isForcingSync, mutate: forceSync } = useMutation(
    () => forceEbaySync(id!),
    mutationOptions,
  );

  const { isLoading: isSyncing, mutate: setSyncStatus } = useMutation(
    (sync: boolean) => postSyncStatus(id!, sync),
    mutationOptions,
  );

  const { isLoading: isUnlinking, mutate: unlinkEbay } = useMutation(
    () => unlinkEbayItem(id!),
    mutationOptions,
  );

  if (!id || !item || !canUseEbaySync) {
    return null;
  }

  const ebay = item.ebay;
  const disabled = ebay?.sync == null || !item.actions.includes('sync_ebay');
  const isAvailable = item.state === 'available';

  return (
    <ListFlowSection title="eBay Sync">
      <Field id="ebay_id" error={errorMap?.ebay_id} label="eBay ID">
        <div className="flex items-center space-x-4">
          <Input
            autoComplete="off"
            id="ebay_id"
            disabled={ebay?.state !== undefined}
            onChange={e => dispatch(changedEbayId({ id: e.target.value }))}
            value={ebayId}
          />

          <LoadingCapableButton
            color="danger"
            disabled={ebay?.state === undefined}
            onClick={() => unlinkEbay()}
            text="Unlink"
            loadingText="Unlinking..."
            loading={isUnlinking}
          />
        </div>
      </Field>

      {isAvailable && (
        <React.Fragment>
          <div className="mb-4 flex items-center space-x-2">
            <div className="text-sm font-semibold">Sync:</div>

            <Toggle
              disabled={isSyncing || disabled}
              value={ebay?.sync ?? false}
              onChange={enabled => setSyncStatus(enabled)}
            />
          </div>

          <LoadingCapableButton
            color="black"
            disabled={disabled}
            size="small"
            onClick={() => forceSync()}
            text="Sync Now"
            loadingText="Syncing..."
            loading={isForcingSync}
          />
        </React.Fragment>
      )}
    </ListFlowSection>
  );
};

export default ListFlowEbaySection;
